import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AlertOctagon, Plus, Search, Filter, Download, FileText, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import NCRModal from "@/components/ncrs/NCRModal";

const STATUS_TONE = {
  Open: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/25",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  Verification: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/25",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  Overdue: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function NCRsPage() {
  const [params, setParams] = useSearchParams();
  const [ncrs, setNcrs] = useState([]);
  const [clients, setClients] = useState([]);
  const [authorized, setAuthorized] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [modal, setModal] = useState({ open: false, id: null });
  const navigate = useNavigate();

  const load = () => {
    const p = {};
    if (clientSlug !== "all") p.client_slug = clientSlug;
    return endpoints.listNcrs(p).then(setNcrs).catch(() => {});
  };

  useEffect(() => {
    endpoints.me().then((u) => {
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
      });
    });
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);

  useEffect(() => {
    if (authorized) load();
    if (clientSlug === "all") { params.delete("client"); setParams(params, { replace: true }); }
    else { params.set("client", clientSlug); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, clientSlug]);

  const filtered = useMemo(() => ncrs.filter((n) => {
    if (status !== "all" && n.status !== status) return false;
    if (q) {
      const hay = `${n.ncr_number} ${n.department} ${n.description} ${n.responsible_person} ${n.iso_clause} ${n.client_name}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [ncrs, q, status]);

  const stats = useMemo(() => {
    const s = { total: ncrs.length, open: 0, in_progress: 0, overdue: 0, closed: 0 };
    ncrs.forEach((n) => {
      if (n.status === "Open") s.open++;
      else if (n.status === "In Progress" || n.status === "Verification") s.in_progress++;
      else if (n.status === "Overdue") s.overdue++;
      else if (n.status === "Closed") s.closed++;
    });
    return s;
  }, [ncrs]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">The NCR register is restricted to ISOQuest staff.</p>
        </div>
      </div>
    );
  }

  const csvHref = endpoints.exportNcrsCsvUrl(clientSlug !== "all" ? { client_slug: clientSlug } : {});

  const StatCard = ({ label, value, tone, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md transition-all card-hover ${active ? "ring-2 ring-indigo-500/50" : ""} border-slate-200/70 dark:border-white/10`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md shadow-orange-500/25">
            <AlertOctagon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">NCRs & Corrective Actions</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {ncrs.length} non-conformity records</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" data-testid="ncr-export-csv-button">
            <a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a>
          </Button>
          <Button data-testid="ncr-new-button" className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, id: null })}>
            <Plus className="h-4 w-4 mr-1" /> New NCR
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Total" value={stats.total} tone="text-indigo-600" testid="ncr-stat-total" onClick={() => setStatus("all")} active={status === "all"} />
        <StatCard label="Open" value={stats.open} tone="text-blue-600" testid="ncr-stat-open" onClick={() => setStatus("Open")} active={status === "Open"} />
        <StatCard label="In progress" value={stats.in_progress} tone="text-amber-600" testid="ncr-stat-in-progress" onClick={() => setStatus("In Progress")} active={status === "In Progress"} />
        <StatCard label="Overdue" value={stats.overdue} tone="text-rose-600" testid="ncr-stat-overdue" onClick={() => setStatus("Overdue")} active={status === "Overdue"} />
        <StatCard label="Closed" value={stats.closed} tone="text-emerald-600" testid="ncr-stat-closed" onClick={() => setStatus("Closed")} active={status === "Closed"} />
      </div>

      {/* Filters */}
      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="ncr-search-input" placeholder="Search NCR #, department, clause, description..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={clientSlug} onValueChange={setClientSlug}>
          <SelectTrigger data-testid="ncr-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="ncr-status-filter" className="h-9 w-[160px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Open">Open</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Verification">Verification</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
            <SelectItem value="Closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Register */}
      <div className="glass-card rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">NCR #</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Dept.</th>
                <th className="text-left px-4 py-3 min-w-[260px]">Description</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Clause</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-center px-4 py-3">Evidence</th>
                <th className="text-center px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => {
                const overdue = n.status === "Overdue";
                return (
                  <tr
                    key={n.id}
                    data-testid={`ncr-row-${n.id}`}
                    onClick={() => setModal({ open: true, id: n.id })}
                    className={`border-t border-slate-100 dark:border-white/5 cursor-pointer transition-colors ${overdue ? "bg-rose-50/60 dark:bg-rose-500/10 hover:bg-rose-100/60" : "hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10"}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {overdue && <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />}
                        {n.ncr_number}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(n.date)}</td>
                    <td className="px-4 py-3 text-xs">{n.client_name}</td>
                    <td className="px-4 py-3 text-xs">{n.department || "—"}</td>
                    <td className="px-4 py-3 text-xs max-w-[320px] truncate" title={n.description}>{n.description}</td>
                    <td className="px-4 py-3 text-xs">{n.source}</td>
                    <td className="px-4 py-3 text-xs font-mono">{n.iso_clause || "—"}</td>
                    <td className="px-4 py-3 text-xs">{n.responsible_person || "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <span className={overdue ? "text-rose-600 font-semibold" : ""}>{fmtDate(n.due_date)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(n.evidence || []).length > 0 ? (
                        <Badge variant="outline" className="text-[10px] rounded-full bg-indigo-50 text-indigo-700 border-indigo-200 gap-1"><FileText className="h-2.5 w-2.5" /> {(n.evidence || []).length}</Badge>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[n.status] || ""}`}>{n.status}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="text-center py-10 text-sm text-slate-500">No NCRs match your filters.</div>}
      </div>

      <NCRModal
        open={modal.open}
        ncrId={modal.id}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        clients={clients}
        onOpenChange={(o) => { if (!o) setModal({ open: false, id: null }); }}
        onChanged={() => { load(); setModal({ open: false, id: null }); }}
      />
    </div>
  );
}
